import { Button, VStack, Text, Flex } from '@chakra-ui/react';
import { NovaIconButton } from 'components/ui/NovaIconButton';
import { ReactElement } from 'react';
import { ModalClassParticipantRemovalVariantProps, SharedModalProps } from 'types/Modal';
import {
  useRemoveStudents,
  useRemoveTeachers,
  ClassParticipantType,
} from 'hooks/api/backend/managament/getClasses';
import { useQueryClient } from '@tanstack/react-query';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { ToastVariant } from 'types/Toast';
import { toastContent } from 'config/toast';
import * as Sentry from '@sentry/react';
import { CloseIcon } from 'assets/icons/v2';
import { CustomModal } from '../CustomModal';
import { CustomModalContent } from '../CustomModalContent';

type Props = SharedModalProps & ModalClassParticipantRemovalVariantProps;

export const ClassParticipantRemovalModal = ({
  handleClose,
  isShown,
  participantId,
  participantName,
  participantType,
  classId,
}: Props): ReactElement => {
  const queryClient = useQueryClient();
  const toast = useNovaToast();
  const { mutateAsync: removeStudents, isPending: isRemovingStudent } = useRemoveStudents();
  const { mutateAsync: removeTeachers, isPending: isRemovingTeacher } = useRemoveTeachers();

  const isLoading = isRemovingStudent || isRemovingTeacher;

  const handleRemove = async (): Promise<void> => {
    if (!participantId || !classId) return;

    try {
      const updatedClass =
        participantType === ClassParticipantType.STUDENT
          ? await removeStudents({ classId, ids: [participantId] })
          : await removeTeachers({ classId, ids: [participantId] });

      // Update cache with the response data instead of refetching
      queryClient.setQueryData(['class', classId], updatedClass);
      queryClient.invalidateQueries({
        queryKey: ['class-participants-search', classId],
      });
      toast.show({
        description:
          participantType === ClassParticipantType.TEACHER
            ? 'Lehrperson erfolgreich entfernt'
            : 'Teilnehmer erfolgreich entfernt',
        variant: ToastVariant.SUCCESS,
      });
      handleClose();
    } catch (error) {
      toast.show({ ...toastContent.error.default, variant: ToastVariant.ERROR });
      Sentry.captureException(error);
    }
  };

  return (
    <CustomModal isOpen={isShown} onClose={handleClose} placement="center" hideHeader size="md">
      <CustomModalContent p="30px">
        <Flex direction="column" gap="30px">
          <Flex direction="column" align="flex-end" gap="5px">
            <NovaIconButton
              aria-label="Schließen"
              variant="primary"
              size="l"
              onClick={handleClose}
              minW="24px"
              h="24px"
              p={0}
              icon={CloseIcon}
              iconProps={{ w: '24px', h: '24px', color: 'text.primary' }}
            />
            <VStack gap="15px" align="flex-start" w="full">
              <Text textStyle="h4.medium" color="text.primary">
                Teilnehmer entfernen
              </Text>
              <Text textStyle="body.m.regular" color="text.primary">
                {participantType === ClassParticipantType.TEACHER
                  ? `Bist du sicher, dass du die Lehrperson ${participantName} aus dem Kurs entfernen möchtest?`
                  : `Bist du sicher, dass du ${participantName} aus dem Kurs entfernen möchtest?`}
              </Text>
            </VStack>
          </Flex>

          <Flex gap="l" justify="flex-end">
            <Button onClick={handleClose} variant="tertiary" px="xl" py="10px" h="auto">
              <Text textStyle="body.m.regular" color="text.secondary">
                Abbrechen
              </Text>
            </Button>
            <Button
              onClick={handleRemove}
              bg="background.error"
              _hover={{ bg: 'text.error', opacity: '90' }}
              _active={{ bg: 'text.error', opacity: '80' }}
              px="xl"
              py="10px"
              h="auto"
              borderRadius="10px"
              loading={isLoading}
            >
              <Text textStyle="body.m.regular" color="grey.white">
                Entfernen
              </Text>
            </Button>
          </Flex>
        </Flex>
      </CustomModalContent>
    </CustomModal>
  );
};
